import React, { useMemo, useRef } from 'react';
import { DropZoneProps, FilePickerProps, StorageManager, FileListFooterProps } from '@aws-amplify/ui-react-storage';
import Box from '@mui/material/Box/Box';
import { Button, Grid, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { StorageManagerDisplayText } from '@aws-amplify/ui-react-storage/dist/types/components/StorageManager/utils/displayText';
import {
	ProcessFile,
	StorageManagerHandle
} from '@aws-amplify/ui-react-storage/dist/types/components/StorageManager/types';
import TableTooltip from '../atoms/TableTooltip';
import { useAppSelector } from '../../../redux/store';

type FileUploadProps = {
	filename?: string;
	accessLevel?: 'public' | 'private' | 'protected';
	onSuccess?: (key: string | undefined) => void;
	// Called when an upload fails (network/permission/size). Lets callers surface
	// feedback instead of the upload silently doing nothing.
	onError?: (message: string) => void;
	acceptedFileTypes?: string[];
	dropFilesText?: string;
	browseFilesText?: string;
	allowedFileTypesText?: string;
	autoUpload?: boolean;
	errors?: boolean;
	maxUploadSize?: string;
	processFile?: ProcessFile;
	fileHeaders?: string[];
	fileSampleRows?: string[][];
	browseButtonSx?: SxProps<Theme>;
	// Hide the floating "Upload Here" label when the surrounding UI already labels the control.
	hideLabel?: boolean;
	// Compact, content-width drop target with centred actions (used for single-image pickers e.g. avatars).
	compact?: boolean;
	// Extra action(s) rendered alongside the Browse button inside the drop target (e.g. a "choose avatar" trigger).
	extraActions?: React.ReactNode;
};

type CustomDisplayText = StorageManagerDisplayText & {
	allowedFileTypesText: string;
};

const DEFAULT_MAX_UPLOAD_BYTES = 6 * 1024 * 1024; // Last-resort fallback if a size string can't be parsed

// Convert a human-readable size string (e.g. "20MB", "4GB", "512KB") into bytes
// so the StorageManager enforces the same limit that is shown to the user.
// Falls back to 6MB when the value is missing or cannot be parsed.
const parseUploadSizeToBytes = (size?: string): number => {
	if (!size) return DEFAULT_MAX_UPLOAD_BYTES;
	const match = /^\s*(\d+(?:\.\d+)?)\s*(KB|MB|GB)?\s*$/i.exec(size);
	if (!match) return DEFAULT_MAX_UPLOAD_BYTES;
	const value = parseFloat(match[1]);
	const unit = (match[2] || 'MB').toUpperCase();
	const multiplier = unit === 'GB' ? 1024 ** 3 : unit === 'KB' ? 1024 : 1024 ** 2;
	return Math.round(value * multiplier);
};

const FileUploader = ({
	onSuccess,
	onError,
	processFile,
	acceptedFileTypes,
	dropFilesText,
	browseFilesText,
	allowedFileTypesText,
	filename,
	accessLevel,
	errors,
	maxUploadSize,
	autoUpload,
	fileHeaders,
	fileSampleRows,
	browseButtonSx,
	hideLabel,
	compact,
	extraActions
}: FileUploadProps) => {
	// Platform-wide default set by an app admin (Admin → System → Uploads). An
	// explicit `maxUploadSize` prop still wins so callers can opt out of it.
	const platformMaxUploadSizeMb = useAppSelector((state) => state.platformSettings.maxUploadSizeMb);
	const effectiveMaxUploadSize = maxUploadSize ?? `${platformMaxUploadSizeMb}MB`;
	const maxFileSize = parseUploadSizeToBytes(effectiveMaxUploadSize);
	const ref = useRef<StorageManagerHandle>(null);

	// The StorageManager remounts any component whose identity changes between
	// renders. Defining these inline in the `components` prop created a new
	// component type on every render, which unmounted and remounted the hidden
	// file <input> — including while the OS file dialog was open. The selected
	// file then landed on a detached input and its change event was lost, so the
	// upload silently never started. Memoising keeps the identities stable.
	const components = useMemo(
		() => ({
			DropZone: (props: DropZoneProps) => (
				<DropZone
					{...props}
					errors={errors}
					hideLabel={hideLabel}
					compact={compact}
					extraActions={extraActions}
					fileHeaders={fileHeaders}
					fileSampleRows={fileSampleRows}
				/>
			),
			FilePicker: (props: FilePickerProps) => <FilePicker {...props} sx={browseButtonSx} />,
			FileListFooter
		}),
		[errors, hideLabel, compact, extraActions, fileHeaders, fileSampleRows, browseButtonSx]
	);

	return (
		<StorageManager
			acceptedFileTypes={acceptedFileTypes ?? []}
			key={filename}
			accessLevel={accessLevel ?? 'protected'}
			maxFileCount={1}
			maxFileSize={maxFileSize}
			autoUpload={autoUpload}
			processFile={processFile}
			ref={ref}
			onUploadSuccess={async ({ key }) => {
				if (onSuccess) {
					onSuccess(key);
				}
			}}
			onUploadError={(message) => {
				if (onError) {
					onError(message);
				}
			}}
			displayText={
				{
					dropFilesText: dropFilesText ?? 'Drop file here',
					browseFilesText: browseFilesText ?? 'Browse',
					errors: errors,
					allowedFileTypesText:
						allowedFileTypesText ??
						/* eslint-disable no-mixed-spaces-and-tabs */
						(acceptedFileTypes
							? `Allowed file types: ${acceptedFileTypes?.join(
									','
							  )} (Max file size: ${effectiveMaxUploadSize})`
							: '')
					/* eslint-enable no-mixed-spaces-and-tabs */
				} as Partial<StorageManagerDisplayText>
			}
			components={components}
		/>
	);
};

const DropZone = ({
	children,
	displayText,
	inDropZone,
	errors,
	hideLabel,
	compact,
	extraActions,
	fileHeaders,
	fileSampleRows,
	...rest
}: DropZoneProps & { errors?: boolean } & { hideLabel?: boolean } & { compact?: boolean } & {
	extraActions?: React.ReactNode;
} & { fileHeaders?: string[] } & {
	fileSampleRows?: string[][];
}) => {
	const theme = useTheme();

	const borderColor = errors
		? theme.palette.error.main
		: inDropZone
		? theme.palette.info.main
		: theme.palette.divider;

	if (compact) {
		return (
			<>
				<Box
					{...rest}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 1,
						width: 'fit-content',
						maxWidth: '100%',
						px: 3,
						py: 2.5,
						borderWidth: '1.5px',
						borderStyle: 'dashed',
						borderColor,
						borderRadius: 2,
						bgcolor: inDropZone ? theme.palette.action.hover : 'transparent',
						transition: 'border-color 0.15s ease, background-color 0.15s ease'
					}}
				>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
						{children}
						{extraActions}
					</Box>
					<Typography variant="caption" color="text.secondary">
						or drag a photo here
					</Typography>
				</Box>
				<Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
					{(displayText as CustomDisplayText).allowedFileTypesText}
				</Typography>
			</>
		);
	}

	return (
		<>
			<Box
				{...rest}
				sx={{
					position: 'relative',
					borderWidth: '1.5px',
					borderStyle: 'dashed',
					borderColor,
					borderRadius: 2,
					bgcolor: inDropZone ? theme.palette.action.hover : 'transparent',
					transition: 'border-color 0.15s ease, background-color 0.15s ease',
					width: 'auto',
					height: 'auto'
				}}
			>
				{!hideLabel && (
					<Typography
						sx={{
							position: 'absolute',
							top: '-9px',
							left: '50px',
							transform: 'translateX(-50%)',
							padding: '0 4px',
							fontSize: '0.75em'
						}}
					>
						Upload Here
					</Typography>
				)}

				<Box sx={{ padding: 2, opacity: 100, display: 'flex', flexDirection: 'row' }}>
					<Grid container justifyContent="space-between" alignItems="center">
						<Grid item>
							<Typography sx={{ width: '100%', wrap: 'wrap', fontSize: '0.9em' }}>
								{displayText.dropFilesText}
							</Typography>
						</Grid>
						<Grid item>
							<Typography>{children}</Typography>
						</Grid>
					</Grid>
				</Box>
			</Box>
			<Typography variant="caption" sx={{ pl: 1 }}>
				{(displayText as CustomDisplayText).allowedFileTypesText}
			</Typography>

			{fileHeaders && <TableTooltip headers={fileHeaders} rows={fileSampleRows}></TableTooltip>}
		</>
	);
};

const FilePicker = ({ onClick, children, sx }: FilePickerProps & { sx?: SxProps<Theme> }) => {
	return (
		<Button
			variant="contained"
			sx={{
				borderRadius: 1,
				padding: '5px 13px',
				fontSize: '0.9em',
				fontWeight: 500,
				textTransform: 'none',
				...sx
			}}
			onClick={onClick}
		>
			{String(children)}
		</Button>
	);
};

export default FileUploader;

const FileListFooter = ({ onClearAll }: FileListFooterProps) => {
	return (
		<Button sx={{ textTransform: 'none' }} variant="text" onClick={onClearAll} size="small">
			Clear All
		</Button>
	);
};
